/* eslint-disable no-console */

import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import { useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import ViewCourseScreen from "../../../components/screens/Courses/ViewCourseScreen";
import {
  courses as courseQuerys,
  classrooms as classroomQuerys,
  completions as completionQuerys,
  roleplays as roleplayQuerys,
  bots as botQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useCourseState from "@nualang/nualang-ui-components/dist/hooks/useCourseState";
import { useTranslation } from "react-i18next";
import AddToClassroom from "@nualang/nualang-ui-components/dist/Dialogs/AddToClassroom";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import CourseImage from "../../../img/course.svg";
import MembersImage from "../../../img/members.svg";
import config, { featureFlags } from "../../../config";
import { UploadCourseImage } from "../../../utils/imageUpload";
import { deleteVectorStore } from "../../../utils/api/CandooAI/apis/AiQuery";
import SelectClassroom from "@nualang/nualang-ui-components/dist/Dialogs/SelectClassroom";
import {
  getCourse,
  updateCourse as updateCourseAPI,
  deleteCourse as deleteCourseAPI,
  getSignedUploadUrl,
  uploadVideo,
  deleteVideo,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  createCourseSectionTopic as createCourseSectionTopicAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  createCourseSection as createCourseSectionAPI,
  updateCourseSection as updateCourseSectionAPI,
  deleteCourseSection as deleteCourseSectionAPI,
  getCourseSections,
} from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getTopics } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  updateCourseSectionTopic as updateCourseSectionTopicAPI,
  moveCourseSectionTopic as moveCourseSectionTopicAPI,
  deleteCourseSectionTopic as deleteCourseSectionTopicAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import {
  createCourseSectionTopicPhraseList as createCourseSectionTopicPhraseListAPI,
} from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import { getMemberCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  getClassrooms,
  getClassroom,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import {
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
  getCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import {
  courses as courseHelpers,
  classrooms as classroomHelpers,
  completions as completionHelpers,
  users as usersHelpers,
  notifications as notificationHelpers,
} from "../../../utils/helpers";
import { trackRecommendedEvent, searchStringToObj } from "../../../utils/index";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import uploadPDF from "../../../utils/pdfUpload";
import { splitFileName } from "../../../utils/fileName";
import { utf8ToB64 } from "../../../utils/index";
import logger from "../../../logger";
import { useApp, useClassroom, useCourse, useSubscription } from "../../../hooks/";
import { UploadTopicImage } from "../../../utils/imageUpload";
import { v4 as uuidv4 } from "uuid";

const { languages, forLanguages, stage, AssetsURL } = config;
export function useAddCourseToClassrooms(func) {
  return useMutation({
    mutationFn: (variables) => {
      return func(variables.courseId, variables.selectedClassrooms);
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: () => { },
  });
}

export function useCreateCourseSectionTopic(func, username) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => {
      return func(variables.courseId, variables.sectionId, variables.values);
    },
    onError: (error) => {
      // An error happened!
      logger.error(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemSections(
          variables.courseId,
          username,
        ),
      });
    },
  });
}

function ViewCourse(props) {
  const { t } = useTranslation();
  const [confirm] = useConfirm(t);
  const queryClient = useQueryClient();
  const {
    authData: { username, attributes = {} },
    handleSignIn,
    authenticated,
    navigate,
    location,
    verificationStatus,
    isCommunityAccessRestricted,
  } = props;
  const params = useParams();
  const { courseId, classroomId } = params;
  const searchParams = searchStringToObj(location.search);
  const { newCollaborator } = searchParams;
  const [communityFilters, setCommunityFilters] = useState({
    difficulty: "all",
    learnLang: "",
    limit: 12,
    visibility: "public",
    verbose: false,
    page: 1,
  });

  const {
    handleOpenModerateDialog,
    openSnackbar,
    startLoading,
    stopLoading,
    loading,
    markCommunityAccessRestricted,
  } = useApp();

  const { subscription, fetchCreatorSubscription } = useSubscription();

  const { setNewCourse, setIsJoinCourseDialogOpen } =
    useCourse();

  // Library topics are only fetched once the Find Topics dialog is first opened.
  const [isLibraryTopicsEnabled, setIsLibraryTopicsEnabled] = useState(false);


  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username },
  );

  const parentClassroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const parentClassroom = useMemo(
    () =>
      parentClassroomQuery.isSuccess &&
        parentClassroomQuery.data &&
        parentClassroomQuery.data.classroomId
        ? parentClassroomQuery.data
        : {},
    [parentClassroomQuery.data, parentClassroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([parentClassroom]);
  }, [parentClassroom, markCommunityAccessRestricted]);

  const { courseSettings } = parentClassroom;

  const classroomMemberQuery = classroomQuerys.useClassroomMember(
    async () => {
      const member = await getClassroomMember(classroomId, username);
      return member;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroomMember =
    classroomMemberQuery.isSuccess &&
      classroomMemberQuery.data &&
      classroomMemberQuery.data.Item
      ? classroomMemberQuery.data.Item
      : {};

  const memberCompletionsQuery = completionQuerys.useMemberCompletions(
    async (memberId) => {
      const memberCompletions = await getMemberCompletions(memberId);
      const formattedCompletions = await completionHelpers.formatCompletions(
        memberCompletions.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedCompletions;
    },
    {
      memberId: username,
    },
    { enabled: !!username },
  );

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { courseName, collaborators } = course;

  const creatorName = course.createdByName;

  useEffect(() => {
    if (course.learnLang && communityFilters.learnLang !== course.learnLang) {
      setCommunityFilters((prev) => ({ ...prev, learnLang: course.learnLang }));
    }
  }, [course.learnLang]);

  const {
    data: communityData = {},
    isLoading: isLoadingCommunityTopics,
    fetchNextPage: fetchNextCommunityPage,
    hasNextPage: hasNextCommunityPage,
    isFetchingNextPage: isFetchingNextCommunityPage,
  } = useInfiniteQuery({
    queryKey: [...courseQuerys.topicKeys.list(communityFilters), "community"],
    queryFn: async ({ pageParam }) => {
      try {
        const topics = await getTopics({
          ...communityFilters,
          page: pageParam.page || 1,
          lastEvaluatedCreatedAt: pageParam.createdAt ? pageParam.createdAt : "",
          lastEvaluatedKey:
            pageParam.topicId && pageParam.courseSectionId
              ? `${pageParam.courseSectionId}/${pageParam.topicId}`
              : "",
          lastEvaluatedExerciseStartTotal:
            pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
              ? pageParam.exerciseStartTotal
              : "",
        });
        const formattedTopics = await courseHelpers.formatTopics(
          topics.Items,
          false,
          props.authData,
        );
        return { ...topics, Items: formattedTopics };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.Items.length > 0 &&
        (lastPage.LastEvaluatedKey?.page ||
          (lastPage.LastEvaluatedKey?.topicId &&
            lastPage.LastEvaluatedKey?.courseSectionId))
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
    enabled: !!communityFilters.learnLang && !isCommunityAccessRestricted,
  });

  const communityTopics = useMemo(
    () => (communityData.pages || []).flatMap((page) => page.Items),
    [communityData],
  );

  // createdBy is applied as a filter on the learnLang index server side, so a
  // single capped request can miss topics - page through them like community.
  const libraryFilters = {
    createdBy: username,
    learnLang: course.learnLang,
    // The default "popular" sort uses an index keyed on exerciseStartTotal,
    // which new (never played) topics don't have yet, so they'd be missing.
    sort: "newest",
    // Also the DynamoDB page size per server-side read, so keep it reasonably large.
    limit: 24,
  };

  const {
    data: libraryData = {},
    isLoading: isLoadingLibraryTopics,
    fetchNextPage: fetchNextLibraryPage,
    hasNextPage: hasNextLibraryPage,
    isFetchingNextPage: isFetchingNextLibraryPage,
    refetch: refetchLibraryTopics,
  } = useInfiniteQuery({
    queryKey: [...courseQuerys.topicKeys.list(libraryFilters), "library"],
    queryFn: async ({ pageParam }) => {
      try {
        const topics = await getTopics({
          ...libraryFilters,
          lastEvaluatedCreatedAt: pageParam.createdAt ? pageParam.createdAt : "",
          lastEvaluatedKey:
            pageParam.topicId && pageParam.courseSectionId
              ? `${pageParam.courseSectionId}/${pageParam.topicId}`
              : "",
          lastEvaluatedExerciseStartTotal:
            pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
              ? pageParam.exerciseStartTotal
              : "",
        });
        const formattedTopics = await courseHelpers.formatTopics(
          topics.Items,
          false,
          props.authData,
        );
        return { ...topics, Items: formattedTopics };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.LastEvaluatedKey?.topicId &&
        lastPage.LastEvaluatedKey?.courseSectionId
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
    enabled: isLibraryTopicsEnabled && !!username && !!course.learnLang,
  });

  const libraryTopics = useMemo(
    () => (libraryData.pages || []).flatMap((page) => page.Items),
    [libraryData],
  );

  const handleFindCourseSectionTopic = () => {
    if (isLibraryTopicsEnabled) {
      // Pick up topics created since the dialog was last opened.
      refetchLibraryTopics();
    } else {
      setIsLibraryTopicsEnabled(true);
    }
  };

  const isStudent =
    attributes?.["custom:role"] !== "teach" &&
    subscription &&
    subscription.isSubscriptionLoaded &&
    !subscription.isPaidUser &&
    !subscription.isUpgradePossible;

  const { membersQuery, sectionsQuery, isMember } = useCourseState({
    courseId,
    username,
    getCourseSections,
    getCourseMember,
    getCourseMembers,
    memberId: username,
    visible: true,
  });

  const courseSections =
    sectionsQuery.isSuccess &&
      Array.isArray(sectionsQuery.data.Items) &&
      sectionsQuery.data.Items.length
      ? sectionsQuery.data.Items.map((section) => ({
          ...section,
          topics: [...(section.topics || [])].sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0)),
        }))
      : [];

  const creatorSubscriptionQuery = userQuerys.useSubscription(
    async (userId) => {
      const response = fetchCreatorSubscription(userId);
      return response;
    },
    {
      userId: course.createdBy,
    },
    {
      enabled: !!course.createdBy,
    },
  );

  const creatorSubscription = useMemo(
    () =>
      creatorSubscriptionQuery.isSuccess &&
        creatorSubscriptionQuery.data &&
        creatorSubscriptionQuery.data.plan
        ? creatorSubscriptionQuery.data
        : {},
    [creatorSubscriptionQuery.data, creatorSubscriptionQuery.isSuccess],
  );

  const classroomCourseIds =
    parentClassroom?.courses?.length > 0
      ? parentClassroom.courses.toString()
      : null;

  // Mutations
  const updateCourseMutation = courseQuerys.useUpdateCourse(
    updateCourseAPI,
    {
      onMutate: () => {
        startLoading();
      },
      onSettled: () => {
        stopLoading();
      },
      onSuccess: async () => {
        openSnackbar("course_updated", "success");
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.list({
            createdBy: username,
            verbose: false,
          }),
        });
        queryClient.invalidateQueries(
          courseQuerys.courseKeys.item(courseId, username),
        );
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
    classroomCourseIds,
  );
  const deleteCourseMutation = courseQuerys.useDeleteCourse(deleteCourseAPI, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: () => {
      openSnackbar("course_deleted", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: username,
          verbose: false,
          limit: config.limits.courses,
        }),
      });
      navigate("/courses#Created");
    },
    onError: () => {
      openSnackbar("problem_deleting", "error");
    },
  });
  const createCourseSectionMutation = courseQuerys.useCreateCourseSection(
    createCourseSectionAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("created", "success");
        // this.handleCloseCreateSectionModal();
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      },
    },
  );
  const updateCourseSectionMutation = courseQuerys.useUpdateCourseSection(
    updateCourseSectionAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const deleteCourseSectionMutation = courseQuerys.useDeleteCourseSection(
    deleteCourseSectionAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("deleted", "success");
      },
      onError: () => {
        openSnackbar("problem_deleting", "error");
      },
    },
  );
  const updateCourseSectionsMutation = courseQuerys.useUpdateCourseSections(
    courseHelpers.updateCourseSections,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const updateCourseSectionTopicMutation =
    courseQuerys.useUpdateCourseSectionTopic(updateCourseSectionTopicAPI, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async () => {
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    });
  const deleteCourseSectionTopicMutation =
    courseQuerys.useDeleteCourseSectionTopic(deleteCourseSectionTopicAPI, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("deleted", "success");
      },
      onError: () => {
        openSnackbar("problem_deleting", "error");
      },
    });
  const duplicateCourseSectionTopicMutation =
    courseQuerys.useDuplicateCourseSectionTopic(courseHelpers.duplicateTopic, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: (data, variables) => {
        queryClient.setQueryData(
          courseQuerys.topicKeys.item(
            variables.courseId,
            variables.sectionId,
            data.topicId,
            username,
          ),
          data,
        );

        queryClient.setQueryData(
          roleplayQuerys.roleplayKeys.list({
            courseSectionTopicId: `${courseId}|${variables.sectionId}|${data.topicId}`,
          }),
          data.roleplays,
        );

        queryClient.setQueryData(
          botQuerys.botKeys.list({
            courseSectionTopicId: `${courseId}|${variables.sectionId}|${data.topicId}`,
          }),
          data.bots,
        );
      },
      onError: () => { },
    }
    );

  const addTopicsToSectionMutation =
    courseQuerys.useDuplicateCourseSectionTopic(
      (values, roleplays, bots, context) =>
        courseHelpers.duplicateTopic(values, roleplays, bots, context, true),
      {
        onSettled: () => {
          stopLoading();
        },
        onSuccess: (data, variables) => {
          queryClient.setQueryData(
            courseQuerys.topicKeys.item(
              variables.courseId,
              variables.sectionId,
              data.topicId,
              username,
            ),
            data,
          );
          queryClient.setQueryData(
            courseQuerys.courseKeys.itemSections(courseId, username),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                Items: old.Items.map((section) =>
                  section.sectionId === variables.sectionId
                    ? { ...section, topics: [...(section.topics || []), data] }
                    : section
                ),
              };
            },
          );
        },
        onError: () => { },
      },
    );

  const updateCourseSectionTopicsMutation =
    courseQuerys.useUpdateCourseSectionTopics(
      courseHelpers.updateCourseSectionTopics,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: () => {
          openSnackbar("updated", "success");
        },
        onError: () => {
          openSnackbar("problem", "error");
        },
      },
    );
  const moveCourseSectionTopicMutation =
    courseQuerys.useMoveCourseSectionTopic(moveCourseSectionTopicAPI, {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    });
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
      openSnackbar("left_course", "success");
      navigate("/courses");
    },
    onError: (error) => {
      logger.error(error);
      openSnackbar("problem", "error");
    },
  });
  const addCourseToClassroomsMutation = useAddCourseToClassrooms(
    classroomHelpers.addCourseToClassrooms,
  );

  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleStartCourse = async () => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId, username });
      }
    }
  };

  const handleDeleteCourse = () => {
    startLoading();
    deleteCourseMutation.mutate({ courseId, username });
  };

  const handleLeaveCourse = () => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleViewCourseTopic = (cId, sectionId, topicId, classroomId) => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${cId}/${sectionId}/${topicId}`);
    } else {
      navigate(`/courses/${cId}/${sectionId}/${topicId}`);
    }
  };

  const handleSearch = isCommunityAccessRestricted ? undefined : () => navigate("/search");

  const handleCreateCourseSection = async (cId, section) => {
    startLoading();
    if (!cId || !section || !section.sectionName) {
      openSnackbar("missing_details", "error");
      return;
    }
    createCourseSectionMutation.mutate({
      courseId: cId,
      values: {
        ...section,
        idx:
          courseSections && Array.isArray(courseSections)
            ? courseSections.length
            : Date.now(),
      },
      username,
    });
  };

  const handleUpdateCourseSection = async (sectionId, values) => {
    startLoading();
    const section = courseSections.find((s) => s.sectionId === sectionId);
    if (values.pdf && values.pdf !== section.pdf) {
      startLoading();
      const [
        url,
        pdfKey,
        vectorStoreFileName,
        hashKey,
        pdfVectorStoreCreationStatus,
      ] = await uploadPDF(values.pdf, "pdf", sectionId);
      if (url) {
        values.pdf = url;
        values.pdfS3Key = pdfKey;
        values.pdfVectorStoreCreationStatus = pdfVectorStoreCreationStatus;
      } else {
        delete values.pdf;
      }
      if (vectorStoreFileName && hashKey && pdfVectorStoreCreationStatus) {
        values.pdfDetails = {
          fileName: vectorStoreFileName,
          key: pdfKey,
          hashKey,
        };
      }
      stopLoading();
    }
    if (values.video && values.video !== course.video) {
      startLoading();
      const { base: videoBase, ext: videoExt } = splitFileName(values.video.name);
      const videoPath = `video/course/${courseId}/section/${sectionId}/${videoBase}-${Date.now()}${videoExt}`;
      const response = await getSignedUploadUrl({
        fileName: videoPath,
        fileType: values.video.type,
      });
      const { url } = response.body;
      await uploadVideo(url, values.video);
      delete values.video;
      values.videoUrl = `${AssetsURL}/${videoPath}`;
      stopLoading();
    }
    updateCourseSectionMutation.mutate({
      courseId,
      sectionId,
      values,
      username,
    });
  };

  const handleUpdateSectionTopic = (cId, sectionId, topicId, values) => {
    startLoading();
    if (!courseId || !sectionId || !topicId) {
      openSnackbar("missing_details", "error");
      return;
    }
    if (values.description && values.description === "") {
      values.description = null;
    }
    updateCourseSectionTopicMutation.mutate({
      courseId: cId,
      sectionId,
      topicId,
      values,
      username,
    });
  };

  const handleDeleteCourseSection = (cId, sectionId, topics) => {
    startLoading();
    deleteCourseSectionMutation.mutate({ courseId: cId, sectionId, username });
    const topicIds = topics.map((topic) => topic?.topicId).filter(Boolean);
    const topicsWithVideo = course?.topicsWithVideo;
    const topicsWithPdf = course?.topicsWithPdf;
    topicIds.forEach((topicId) => {
      if (topicsWithVideo && topicsWithVideo[topicId])
        delete topicsWithVideo[topicId];
      if (topicsWithPdf && topicsWithPdf[topicId])
        delete topicsWithPdf[topicId];
    });
    updateCourseMutation.mutate({
      courseId,
      values: { topicsWithVideo, topicsWithPdf },
      username,
    });
  };

  const handleCreateCourseSectionTopic = async (sectionId) => {
    if (classroomId) {
      navigate(`/classrooms/${classroomId}/${courseId}/${sectionId}/create`);
    } else {
      navigate(`/courses/${courseId}/${sectionId}/create`);
    }
  };

  const handleDeleteCourseSectionTopic = (cId, sectionId, topicId) => {
    startLoading();
    deleteCourseSectionTopicMutation.mutate({
      courseId: cId,
      sectionId,
      topicId,
      username,
    });
    const topicsWithVideo = course?.topicsWithVideo;
    if (topicsWithVideo && topicsWithVideo[topicId])
      delete topicsWithVideo[topicId];
    const topicsWithPdf = course?.topicsWithPdf;
    if (topicsWithPdf && topicsWithPdf[topicId]) delete topicsWithPdf[topicId];
    updateCourseMutation.mutate({
      courseId,
      values: { topicsWithVideo, topicsWithPdf },
      username,
    });
  };

  const handleDuplicateTopic = (courseId, sectionId, topicId, topic) => {
    startLoading();
    duplicateCourseSectionTopicMutation.mutate({
      values: window.structuredClone(topic),
      roleplays: window.structuredClone(topic.roleplays),
      bots: window.structuredClone(topic.bots),
      context: "view-course",
      username,
      courseId,
      sectionId,
      topicId,
    });
  };

  const handleAddTopicsToSection = (selectedTopics, sectionId) => {
    const idx = Date.now();
    selectedTopics.map((topic) => {
      startLoading();
      addTopicsToSectionMutation.mutate({
        values: {
          ...window.structuredClone({...topic, idx }),
          courseSectionId: `${courseId}|${sectionId}`,
        },
        roleplays: [],
        bots: [],
        context: "view-course",
        username,
        courseId,
        sectionId,
        topicId: topic.topicId,
      });
    });
  };

  const handleUpdateCourse = async (values, { setSubmitting, resetForm }) => {
    try {
      if (
        values.visibility &&
        values.visibility !== "public" &&
        values.visibility !== "private"
      ) {
        throw new Error();
      }
      if (values.collaborators) {
        notificationHelpers.sendCollaboratorEmail(
          values.collaborators,
          courseName,
          "course",
          collaborators,
          courseId,
          creatorName,
        );
      }
      if (values.picture && values.picture !== course.picture) {
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadCourseImage(base64String[1] || base64String[0], courseId);
        if (moderated || warn) {
          if (verificationStatus === "approved") {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.banner && values.banner !== course.banner) {
        const base64String = values.banner.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadCourseImage(base64String[1] || base64String[0], courseId);
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.bannerWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.banner = picture;
          values.bannerS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.pdf && values.pdf !== course.pdf) {
        startLoading();
        const [
          url,
          pdfKey,
          vectorStoreFileName,
          hashKey,
          pdfVectorStoreCreationStatus,
        ] = await uploadPDF(values.pdf, "pdf", courseId);
        if (url) {
          values.pdf = url;
          values.pdfS3Key = pdfKey;
          values.pdfVectorStoreCreationStatus = pdfVectorStoreCreationStatus;
        } else {
          delete values.pdf;
        }
        if (vectorStoreFileName && hashKey && pdfVectorStoreCreationStatus) {
          values.pdfDetails = {
            fileName: vectorStoreFileName,
            key: pdfKey,
            hashKey,
          };
        }
        stopLoading();
      }
      if (values.video && values.video !== course.video) {
        startLoading();
        const { base: videoBase, ext: videoExt } = splitFileName(values.video.video.name);
        const videoPath = `video/course/${courseId}/${videoBase}-${Date.now()}${videoExt}`;
        const response = await getSignedUploadUrl({
          fileName: videoPath,
          fileType: values.video.video.type,
        });
        const { url } = response.body;
        await uploadVideo(url, values.video.video);
        delete values.video;
        values.videoUrl = `${AssetsURL}/${videoPath}`;
        stopLoading();
      }
      updateCourseMutation.mutate({ courseId, values, username });
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    } catch (error) {
      if (resetForm) {
        resetForm({ values });
      }
      if (setSubmitting) {
        setSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (newCollaborator) {
      invalidateCache(DataType.COURSE_COLLABORATOR_JOIN, {
        courseId,
      });
    }
  }, []);

  const handleUpdateSections = (sections) => {
    startLoading();
    updateCourseSectionsMutation.mutate({ courseId, sections, username });
  };

  const handleUpdateTopics = (sectionId, topics) => {
    startLoading();
    updateCourseSectionTopicsMutation.mutate({
      courseId,
      sectionId,
      topics,
      username,
    });
  };

  const handleMoveTopic = ({
    sourceSectionId,
    targetSectionId,
    topic,
    targetTopics,
    sourceTopics,
  }) => {
    startLoading();
    // Position the moved topic within the target section based on where it was
    // dropped; the mutation optimistically re-parents it and rolls back on error.
    const idx = targetTopics.findIndex((t) => t.topicId === topic.topicId);
    moveCourseSectionTopicMutation.mutate({
      courseId,
      sourceSectionId,
      targetSectionId,
      topicId: topic.topicId,
      idx: idx > -1 ? idx : targetTopics.length,
      targetTopics,
      sourceTopics,
      username,
    });
  };

  const handleDeletePDF = async () => {
    try {
      startLoading();
      const values = {
        pdf: "",
        pdfS3Key: "",
        pdfDetails: "",
        pdfVectorStoreCreationStatus: "",
      };
      if (
        course &&
        course.pdfDetails &&
        course.pdfVectorStoreCreationStatus === "CREATION_COMPLETE" &&
        stage === "dev"
      ) {
        const { fileName, hashKey } = course.pdfDetails;
        const body = {
          itemsToDelete: [
            {
              fileName,
              hashKey,
            },
          ],
          type: "file",
        };
        await deleteVectorStore(body);
      }
      updateCourseMutation.mutate({ courseId, values, username });
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const handleDeleteSectionPDF = async (sectionId) => {
    try {
      const section = courseSections.find((s) => s.sectionId === sectionId);
      startLoading();
      const values = {
        pdf: "",
        pdfS3Key: "",
        pdfDetails: "",
        pdfVectorStoreCreationStatus: "",
      };
      if (
        section &&
        section.pdfDetails &&
        section.pdfVectorStoreCreationStatus === "CREATION_COMPLETE" &&
        stage === "dev"
      ) {
        const { fileName, hashKey } = section.pdfDetails;
        const body = {
          itemsToDelete: [
            {
              fileName,
              hashKey,
            },
          ],
          type: "file",
        };
        await deleteVectorStore(body);
      }
      updateCourseSectionMutation.mutate(
        { courseId, sectionId, values, username },
        {
          onSettled: () => {
            // Error or success... doesn't matter!
            stopLoading();
          },
          onSuccess: () => {
            openSnackbar("updated", "success");
          },
          onError: () => {
            openSnackbar("problem", "error");
          },
        },
      );
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const handleDeleteCourseVideo = async (s3Path) => {
    try {
      startLoading();
      const values = {
        videoUrl: "",
      };
      if (s3Path) {
        logger.info({ s3Path });
        const parts = s3Path.split("nualang.com");
        logger.info({ parts });
        let videoPathOnly = parts[1].startsWith("/")
          ? parts[1].substring(1)
          : parts[1];
        videoPathOnly = utf8ToB64(videoPathOnly); // base64 encode the video path
        await deleteVideo(videoPathOnly);
      }
      updateCourseMutation.mutate(
        { courseId, values, username },
        {
          onSuccess: async () => {
            openSnackbar("course_updated", "success");
            queryClient.invalidateQueries(
              courseQuerys.courseKeys.item(courseId, username),
            );
            stopLoading();
          },
          onError: () => {
            openSnackbar("problem", "error");
            stopLoading();
          },
        },
      );
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const [isAddToClassroomOpen, setIsAddToClassroomOpen] = useState(false);

  const handleOpenAddToClassroom = () => setIsAddToClassroomOpen(true);

  const handleCloseAddToClassroom = () => setIsAddToClassroomOpen(false);

  const courseFound = course && course.courseId;

  const { setQuickCreateClassroomOpen } = useClassroom();

  const handleCreateClassroom = () => setQuickCreateClassroomOpen(true);

  const handleAddToClassroom = (selectedClassrooms) => {
    startLoading();
    try {
      addCourseToClassroomsMutation.mutate(
        { courseId, selectedClassrooms },
        {
          onSettled: () => {
            // Error or success... doesn't matter!
            stopLoading();
          },
          onSuccess: () => {
            openSnackbar("classroom_updated", "success");
            queryClient.invalidateQueries({
              queryKey: classroomQuerys.classroomKeys.list({
                createdBy: username,
                verbose: false,
                limit: config.limits.teachClassrooms,
              }),
            });
            for (
              let index = 0;
              index < (selectedClassrooms || []).length;
              index++
            ) {
              const cId = selectedClassrooms[index];
              queryClient.invalidateQueries({
                queryKey: classroomQuerys.classroomKeys.item(cId, username),
              });
            }
            handleCloseAddToClassroom();
          },
          onError: () => {
            openSnackbar("problem", "error");
          },
        },
      );
    } catch (error) {
      logger.error(error);
    }
  };
  const [isSelectClassroomDialogOpen, setIsSelectClassroomDialogOpen] =
    useState(false);

  const handleOpenSelectClassroomDialog = () => {
    setIsSelectClassroomDialogOpen(true);
  };

  const handleCloseSelectClassroomDialog = () => {
    setIsSelectClassroomDialogOpen(false);
  };

  const teachClassrooms =
    teachClassroomsQuery.isSuccess &&
      Array.isArray(teachClassroomsQuery.data) &&
      teachClassroomsQuery.data.length
      ? teachClassroomsQuery.data.filter((c) => c.visibility !== "archived")
      : [];

  const createCourseSectionTopicMutation = useCreateCourseSectionTopic(
    createCourseSectionTopicAPI,
    username,
  );

  const createCourseSectionTopicPhraseListMutation =
    courseQuerys.useCreateCourseSectionTopicPhraseList(
      createCourseSectionTopicPhraseListAPI,
    );

  const createCourseSectionTopic = (
    courseId,
    sectionId,
    values,
  ) => {
    if (!courseId || !sectionId || !values || !values.topicName) {
      openSnackbar("missing_details", "error");
      return;
    }
    startLoading();
    const idx = Date.now();
    return createCourseSectionTopicMutation.mutateAsync(
      {
        courseId,
        sectionId,
        values: {
          ...values,
          phrases: [],
          idx,
        },
      },
      {
        onSuccess: async (data) => {
          let newPhrases = [],
            newRoleplays = [],
            newBots = [];

          await invalidateCache(DataType.TOPIC_DUPLICATE, {
            courseId,
            sectionId,
            topicId: data.Item.topicId,
          });

          stopLoading();
          openSnackbar("created", "success");

          queryClient.setQueryData(
            courseQuerys.topicKeys.item(
              courseId,
              sectionId,
              data.Item.topicId,
              username,
            ),
            {
              ...data.Item,
              phrases: newPhrases,
              roleplays: newRoleplays,
              bots: newBots,
            },
          );


          queryClient.invalidateQueries({
            queryKey: courseQuerys.topicKeys.item(
              courseId,
              sectionId,
              data.Item.topicId,
              username
            ),
          });

          queryClient.setQueryData(
            roleplayQuerys.roleplayKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.Item.topicId}`,
            }),
            newRoleplays,
          );

          queryClient.setQueryData(
            botQuerys.botKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${data.Item.topicId}`,
            }),
            newBots,
          );

          queryClient.setQueryData(
            courseQuerys.courseKeys.itemSections(courseId, username),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                Items: old.Items.map((section) => {
                  if (section.sectionId !== sectionId) return section;
                  const topics = section.topics || [];
                  if (topics.some((t) => t.topicId === data.Item.topicId)) {
                    return section;
                  }
                  return { ...section, topics: [...topics, { idx, ...data.Item }] };
                }),
              };
            },
          );

          try {
            await createCourseSectionTopicPhraseListMutation.mutateAsync({
              courseId,
              sectionId,
              topicId: data.Item.topicId,
              values: {
                phraseListName: values.topicName,
                description: "",
                ...(values.picture && { picture: values.picture }),
                ...(values.pictureS3Key && { pictureS3Key: values.pictureS3Key }),
              },
              username,
            });
          } catch (phraseListError) {
            logger.error(phraseListError);
          }

          if (data && data.Item && data.Item.topicId) {
            const { topicId } = data.Item;
            if (classroomId && courseId && sectionId && topicId) {
              navigate(
                `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
              );
            } else if (courseId && sectionId && topicId) {
              navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
            } else {
              navigate("/courses");
            }
          }
        },
        onError: () => {
          stopLoading();
          openSnackbar("problem_creating", "error");
        },
      },
    );
  };

  const handleQuickCreateCourseSectionTopic = async (values, sectionId) => {
    try {
      // Remove all null values recursively
      const removeNulls = (obj) => {
        if (Array.isArray(obj)) {
          return obj
            .map(removeNulls)
            .filter((value) => value !== null && value !== undefined);
        }

        if (obj && typeof obj === "object") {
          return Object.keys(obj).reduce((acc, key) => {
            const value = removeNulls(obj[key]);

            if (value !== null && value !== undefined) {
              acc[key] = value;
            }

            return acc;
          }, {});
        }

        return obj;
      };

      values = removeNulls(values);
      const { visibility, difficulty, tags } = values;
      if (
        (visibility !== "public" && visibility !== "private") ||
        !Array.isArray(tags)
      ) {
        //input validation
        throw new Error();
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadTopicImage(base64String[1] || base64String[0], uuidv4());
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          const originalPicture = values.picture;
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
          // The quick-create dialog seeds banner from the same image as picture
          // (a base64 data URL). Only when the banner is that seeded image do we
          // reuse the uploaded S3 URL, avoiding overwriting a user-chosen banner
          // and avoiding persisting raw base64 (which would exceed DynamoDB's
          // 400KB item limit).
          if (values.banner === originalPicture) {
            values.banner = picture;
            values.bannerS3Key = pictureS3Key;
          }
        } else {
          delete values.picture;
        }
      }
      delete values.topicId;
      delete values.exerciseCompletionTotal;
      delete values.exerciseStartTotal;

      await createCourseSectionTopic(
        courseId,
        sectionId,
        values,
      );
    } catch (error) {
      openSnackbar("Error creating topic", "error");
      logger.error(error);
    }
  };


  return (
    <>
      {courseQuery.isLoading ? (
        <ViewCourseScreen
          t={t}
          isLoading={courseQuery.isLoading}
          isSectionsLoading={sectionsQuery.isLoading}
          placeholderImageUrl={CourseImage}
          course={{}}
        />
      ) : (
        <ViewCourseScreen
          t={t}
          isLoading={courseQuery.isLoading}
          isSectionsLoading={sectionsQuery.isLoading}
          placeholderImageUrl={CourseImage}
          memberPlaceholderImageUrl={MembersImage}
          course={courseFound ? course : null}
          isMember={isMember}
          sections={courseSections}
          members={
            membersQuery.isSuccess &&
              Array.isArray(membersQuery.data.Items) &&
              membersQuery.data.Items.length
              ? membersQuery.data.Items
              : []
          }
          memberCompletions={
            memberCompletionsQuery.isSuccess &&
              Array.isArray(memberCompletionsQuery.data) &&
              memberCompletionsQuery.data.length
              ? memberCompletionsQuery.data
              : []
          }
          handleLeaveCourse={handleLeaveCourse}
          handleDeleteCourse={handleDeleteCourse}
          handleStartCourse={handleStartCourse}
          handleViewCourseTopic={handleViewCourseTopic}
          handleCreateCourseSection={handleCreateCourseSection}
          handleDeleteCourseSection={handleDeleteCourseSection}
          handleUpdateCourseSection={handleUpdateCourseSection}
          handleUpdateSectionTopic={handleUpdateSectionTopic}
          handleDuplicateTopic={handleDuplicateTopic}
          handleCreateCourseSectionTopic={handleCreateCourseSectionTopic}
          handleDeleteCourseSectionTopic={handleDeleteCourseSectionTopic}
          handleSearch={handleSearch}
          handleOpenAddToClassroom={handleOpenAddToClassroom}
          languages={languages}
          forLanguages={forLanguages}
          handleUpdateCourse={handleUpdateCourse}
          handleUpdateSections={handleUpdateSections}
          handleUpdateTopics={handleUpdateTopics}
          handleMoveTopic={handleMoveTopic}
          getMemberDetails={getMemberDetails}
          fileSizeLimit={config.fileSizeLimit}
          navigate={navigate}
          subscription={subscription}
          creatorSubscription={creatorSubscription}
          membersProps={{
            handleViewActivity,
            showSortControl: true,
          }}
          handleViewUserProfile={handleViewUserProfile}
          handleOpenSelectClassroomDialog={handleOpenSelectClassroomDialog}
          isStudent={isStudent}
          teachClassrooms={teachClassrooms}
          mainSiteUrl={config.SITE_URL}
          attributes={attributes}
          trackRecommendedEvent={trackRecommendedEvent}
          handleSignIn={handleSignIn}
          authenticated={authenticated}
          addCourseToClassroom={handleAddToClassroom}
          openSnackbar={openSnackbar}
          pdfUrl={course.pdf}
          videoUrl={course.videoUrl}
          courseSections={courseSections}
          loading={loading}
          pdfTitle={courseName}
          handleDeletePDF={handleDeletePDF}
          handleDeleteCourseVideo={handleDeleteCourseVideo}
          handleDeleteSectionPDF={handleDeleteSectionPDF}
          courseSettings={courseSettings?.[courseId]}
          classroomId={classroomId}
          featureFlags={featureFlags}
          isChallengeModeStudent={
            classroomMember?.assignedLabels?.isChallengeModeStudent
          }
          isReadWriteModeStudent={
            classroomMember?.assignedLabels?.isReadWriteModeStudent
          }
          handleQuickCreateCourseSectionTopic={handleQuickCreateCourseSectionTopic}
          handleAddTopicsToSection={handleAddTopicsToSection}
          handleFindCourseSectionTopic={handleFindCourseSectionTopic}
          libraryTopics={libraryTopics}
          isLoadingLibraryTopics={isLoadingLibraryTopics}
          onLoadMoreLibrary={fetchNextLibraryPage}
          hasMoreLibrary={hasNextLibraryPage}
          isLoadingMoreLibrary={isFetchingNextLibraryPage}
          communityTopics={communityTopics}
          isLoadingCommunityTopics={isLoadingCommunityTopics}
          onLoadMoreCommunity={fetchNextCommunityPage}
          hasMoreCommunity={hasNextCommunityPage}
          isLoadingMoreCommunity={isFetchingNextCommunityPage}
          isCommunityAccessRestricted={isCommunityAccessRestricted}
        />
      )}
      <AddToClassroom
        t={t}
        courseId={courseId}
        open={isAddToClassroomOpen}
        handleClose={handleCloseAddToClassroom}
        handleSubmit={handleAddToClassroom}
        title={t("add_to_classroom")}
        desc={t("add_to_classroom_desc", {
          courseName: course.courseName,
          classroomNames: "...",
        })}
        teachClassrooms={teachClassrooms}
        handleCreateClassroom={handleCreateClassroom}
      />
      <SelectClassroom
        t={t}
        open={isSelectClassroomDialogOpen}
        handleClose={handleCloseSelectClassroomDialog}
        title={t("select_a_classroom")}
        type={"course"}
        courseId={courseId}
        teachClassrooms={teachClassrooms.filter((classroom) =>
          classroom?.courses?.includes(courseId),
        )}
      />
    </>
  );
}

ViewCourse.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};
export default ViewCourse;
